import React, { FC, useContext, useState } from 'react';
import { nanoid } from 'nanoid';
import InputField from '@components/FormFields/InputField/InputField';
import CheckBox from '@components/FormFields/CheckBox';
import PlusCircle from '@components/Icons/PlusCircle';
import Buttons from '@components/Buttons';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import teamsContext from '@providers/Teams/TeamsContext';
import s from '@components/CreateEditDocumentRequestForm/DocumentRequestForm.module.scss';
import Spacer from '@components/Spacer';
import DocumentFileCard from '@components/Document/DocumentFileCard';
import Divider from '@components/Divider';
import { DocumentRequest, DocumentRequestFile, DocumentRequestFileStatus } from '@typings/document-api-models';

type Props = {
  onSubmit?: () => Promise<void>;
  documentRequest?: Pick<DocumentRequest, 'id' | 'title' | 'isFollowupEnabled'> & {
    files: Pick<DocumentRequestFile, 'id' | 'fileName' | 'fileDescription' | 'isRequired'>[];
  } | null;
};

const DocumentRequestForm: FC<Props> = ({ onSubmit, documentRequest }) => {
  const [documents, setDocuments] = useState(documentRequest
    ? documentRequest.files.map((f) => ({
      description: f.fileDescription,
      id: f.id,
      isCollapsed: true,
      isRequired: f.isRequired,
      title: f.fileName,
    }))
    : [{
      description: '',
      id: nanoid(),
      isCollapsed: false,
      isRequired: true,
      title: '',
    }],
  );
  const [isFollowupEnabled, setFollowupEnabled] = useState(documentRequest?.isFollowupEnabled || false);
  const [title, setTitle] = useState(documentRequest?.title || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { creatorId } = useContext(teamsContext);

  const { getData: createDocumentRequestFn } = useNoodleApi(tsClient.createDocumentRequest);
  const { getData: updateDocumentRequestFn } = useNoodleApi(tsClient.updateDocumentRequestTemplate);

  const addEmptyDocument = (): void => {
    setDocuments(prev => [...prev, {
      description: '',
      id: nanoid(),
      isCollapsed: false,
      isRequired: true,
      title: '',
    }]);
  };

  const removeDocument = (index: number): void => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (): Promise<void> => {
    if (creatorId) {
      setIsSubmitting(true);
      if (documentRequest) {
        await updateDocumentRequestFn({
          id: documentRequest.id,
          updates: {
            files: documents.map((d) => ({
              fileDescription: d.description,
              fileName: d.title,
              id: d.id,
            })),
            isFollowupEnabled,
            title,
          },
        });
      } else {
        await createDocumentRequestFn({
          creatorId,
          files: documents,
          isFollowupEnabled,
          title,
        });
      }
      if (onSubmit) {
        await onSubmit();
      }
      setIsSubmitting(false);
    }
  };

  const handleChange = (documentId: string) => (key: string, value: string | boolean): void => {
    setDocuments(prev => prev.map((document) => {
      if (documentId === document.id) {
        return {
          ...document,
          [key]: key === 'isRequired' ? !document.isRequired : value,
        };
      }
      return document;
    }));
  };

  const areDocumentsComplete = (): boolean => {
    for (let i = 0; i < documents.length; i += 1) {
      if (!documents[i].title) {
        return false;
      }
    }
    return true;
  };

  return (
    <div className={s.container}>
      <InputField
        id='title'
        name='title'
        values={{ title }}
        onChange={setTitle}
        label='Title of document request'
        hasFixedHeight
      />
      <CheckBox
        id={'isFollowupEnabled'}
        name={'isFollowupEnabled'}
        values={{ isFollowupEnabled }}
        onChange={() => setFollowupEnabled(prev => !prev)}
        label='Follow up automatically until completed'
        isSwitch
        hasFixedHeight={false}
      />
      <Spacer size={12} />
      <Divider />
      <Spacer size={16} />
      <p className='caption-bold'>Documents</p>
      <Spacer size={16} />
      {documents.map((document, index) => (
        <>
          {document.isCollapsed
            ? (
              <Buttons isFullWidth isWrapper onClick={() => handleChange(document.id)('isCollapsed', false)}>
                <DocumentFileCard
                  status={DocumentRequestFileStatus.INCOMPLETE}
                  fileDescription={document.description}
                  fileName={document.title}
                  isEdit
                  isRequired={document.isRequired}
                />
              </Buttons>
            )
            : (
              <div className={s.document}>
                <InputField
                  id='title'
                  name='title'
                  values={{ title: document.title }}
                  onChange={handleChange(document.id).bind(null, 'title')}
                  label='Title of document'
                  hasFixedHeight
                />
                <InputField
                  id='description'
                  name='description'
                  rows={3}
                  values={{ description: document.description }}
                  onChange={handleChange(document.id).bind(null, 'description')}
                  label='Description'
                />
                <Spacer size={16} />
                <CheckBox
                  disabled={Boolean(documentRequest)}
                  id={`${document.id}isRequired`}
                  name={`${document.id}isRequired`}
                  values={{ [`${document.id}isRequired`]: document.isRequired }}
                  onChange={handleChange(document.id).bind(null, 'isRequired')}
                  label={documentRequest ? (document.isRequired ? 'Required' : 'Optional') : 'Required'}
                  isSwitch
                  hasFixedHeight={false}
                />
                {documents.length > 1 && !documentRequest
                  && <Buttons isWrapper className={s.remove} onClick={() => removeDocument(index)}>Remove</Buttons>}
              </div>
            )
          }
          <Spacer size={16} />
        </>
      ))}
      {!documentRequest && (
        <>
          <Buttons isWrapper onClick={addEmptyDocument}>
            <PlusCircle weight="fill" size={24} color="var(--color-noodle)" />
            <p className='caption-bold'>Add document</p>
          </Buttons>
          <Spacer size={16} />
        </>
      )}
      <Buttons
        disabled={!creatorId || !title || !areDocumentsComplete()}
        isFetching={isSubmitting}
        isSecondary
        isFullWidth
        onClick={handleSubmit}
      >
        Submit
      </Buttons>
    </div>
  );
};

export default DocumentRequestForm;
